import Vue from 'vue'
import Vuex from 'vuex'
import createCache from 'vuex-cache'

import {
  getItemComments,
  openLoginModal,
  openSignupModal,
  destroy as destroyComment,
  create as createComment,
  update as updateComment
} from '@/src/actions/comment_actions';

import {
  createInvitation,
  getInvitations
} from '@/src/actions/invitation_actions';

import { getFeatures } from '@/src/actions/general_actions';
import { getConfiguration } from '@/src/actions/configuration_actions';
import { updateItem, getItem, destroyItem, annotate, favoriteItem } from '@/src/actions/item_actions';
import { getCurrentOrganization, reloadCurrentOrganization } from '@/src/actions/organization_actions';
import { addItemReaction, removeItemReaction } from '@/src/actions/reaction_actions';
import { searchTemplates, applyTemplate } from '@/src/actions/template_actions.js';
import { getUser, searchUsers } from '@/src/actions/user_actions';
import { searchCallToActions, getCallToAction } from '@/src/actions/call_to_action_actions.js';
import { createSubscription } from '@/src/actions/subscription_actions';
import { createTranscription, getTranscription, retryTranscription } from '@/src/actions/transcription_actions';
import { createLicenseRequest, getLicenseRequests } from '@/src/actions/license_request_actions';
import { getCampaignModals, deleteCampaignModals } from "@/src/actions/campaign_modal_actions";
import { getAllCollections } from "@/src/actions/collection_actions.js";
import { disableSimplifiedInterface } from "@/src/actions/simplified_interface_actions";

import { getAnalytics, updateStats } from "@/src/actions/analytics_actions";

Vue.use(Vuex);

const store = new Vuex.Store({
  plugins: [createCache()],
  state: {
    gon: window.gon,
    item: gon.item,
    annotatedImage: null,
    currentOrganization: { id: undefined, attributes: {} },
    itemOrganization: gon.itemOrganization,
    transcription: { id: null, attributes: {} },
    features: {},
    featuresLoaded: false,
    user: { id: null, attributes: {} },
    comments: [],
    stats: [],
    selectedMobileOption: "overview",
    call_to_action: {
      id: null,
      attributes: {
        text: "My Custom Button",
        link: null,
        appearance: "cta-button-below",
        button_color: "#007AFF",
        text_color: "#ffffff",
        corner_style: "cta-rounded",
        display_action: true,
      },
    },
    disableSimplifiedInterface: JSON.parse(localStorage.getItem("simplified-interface-hidden") || false)
  },
  mutations: {
    setGonUserAchievements: (state, user_onboarding_achievements) => state.gon.user_onboarding_achievements = user_onboarding_achievements,
    setCurrentOrganization: (state, params) => (state.currentOrganization = params),
    setComments: (state, params) => (state.comments = params || []),
    setStats: (state, params) => (state.stats = params || []),
    setItem: (state, params) => (state.item = params || {}),
    setItemOrganization: (state, params) => (state.itemOrganization = params || {}),
    setFeatures: (state, params) => {
      state.features = params;
      state.featuresLoaded = true;
    },
    setAnnotatedImage: (state, params) => (state.annotatedImage = params || null),
    setCallToAction: (state, params) => (state.call_to_action = params || {}),
    setOpenModal: (state, params) => (state.openModal = params || false),
    setCampaignModals: (state, params) => (state.campaignModals = params || null),
    setUser: (state, params) => (state.user = params),
    selectMobileOption: (state, params) => (state.selectedMobileOption = params),
    updateItem: (state, params) => (state.item = { ...state.item, ...params } || {}),
    favoriteItem: (state, params) => (state.item.attributes.favorite = params),
    setTranscription: (state, params) => (state.transcription = { ...state.transcription, ...params } || {}),
    updateCallToAction: (state, params) => (state.call_to_action = { ...state.call_to_action, ...params } || {}),
    setCollections: (state, params) => (state.collections = params || []),
    addCollections: (state, params) => (state.collections += params),
    disableSimplifiedInterface: (state) =>(state.disableSimplifiedInterface = true),
  },
  actions: {
    addItemReaction,
    removeItemReaction,
    annotate,
    getCurrentOrganization,
    reloadCurrentOrganization,
    createInvitation,
    getFeatures,
    getInvitations,
    getItemComments,
    destroyComment,
    createComment,
    updateComment,
    getAnalytics,
    updateStats,
    getItem,
    destroyItem,
    favoriteItem,
    getUser,
    searchUsers,
    updateItem,
    openLoginModal,
    openSignupModal,
    searchTemplates,
    applyTemplate,
    getConfiguration,
    searchCallToActions,
    getCallToAction,
    createSubscription,
    createTranscription,
    getTranscription,
    retryTranscription,
    createLicenseRequest,
    getLicenseRequests,
    getCampaignModals,
    deleteCampaignModals,
    getAllCollections,
    disableSimplifiedInterface,
  },
  getters: {
    groupedCount: function(state, getters) {
      let count = 0;
      for (const key in getters.groupedStats) {
        count += getters.groupedStats[key].length;
      }
      return count;
    },
    groupedStats: function(state) {
      const groupedStats = {};

      state.stats.forEach((stat) => {
        const attrs = stat.attributes;

        const key = attrs.email;
        const percentage = attrs.data.viewed_pc ? Number.parseInt(attrs.data.viewed_pc) : 0;

        groupedStats[key] = groupedStats[key] || [];

        if (key == "Anonymous User") {
          groupedStats[key].push({ percentage, created_at: attrs.created_at });
        } else if (percentage >= (groupedStats[key][0]?.percentage || 0)) {
          groupedStats[key] = [{ percentage, created_at: attrs.created_at }];
        }
      });

      return groupedStats;
    },
    showSimplifiedInterface: function(state) {
      const viewer_show_first_time_features = gon.viewer_show_first_time_features;
      const item_count = gon.currentUserItemsCount;
      const comments_exists = gon.item.attributes.comments_size > 0;
      const items_threshold = 5; // We want to introduce rich interface after 5th drop.

      if (state.disableSimplifiedInterface || !viewer_show_first_time_features || comments_exists) {
        return false;
      }
      // User not logged in
      if (typeof item_count === 'undefined' || item_count === null) {
        return false;
      }
      return item_count < items_threshold;
    },
  },
});

export default store;
